import { useSelector } from 'react-redux';

import { Button, EButtonType } from '@/components/Button';
import { ModalComponent } from '@/components/ModalComponent';
import { ModalHead } from '@/components/ModalHead';
import { ModalHeader } from '@/components/ModalHeader';
import { ModalSubHeader } from '@/components/ModalSubHeader';
import {
    ELoanUsage,
    EVehicleAssetType,
    selectAddressByIndex,
    selectEmail,
    selectFirstName,
    selectLoanUsage,
    selectVehicleAssetType,
} from '@/context/borrower';
import {
    EModals,
    selectAgreedToTermsAndConditions,
    selectSignupOptions,
    setModal,
} from '@/context/display';
import { RootState } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { Email } from '@/UserInput/Email';
import { FirstName } from '@/UserInput/FirstName';
import { LoginCode } from '@/UserInput/LoginCode';
import { MobileNumber } from '@/UserInput/MobileNumber';
import { PostCode } from '@/UserInput/PostCode';
import { TermsAndConditions } from '@/UserInput/TermsAndConditions';
import { isEmail } from '@/utils/isEmail';

export const testId = 'Register';

export function Register(): JSX.Element {
    // ***** Constants *****
    const baseId = 'modal-register';

    // ***** Redux State *****
    const dispatch = useAppDispatch();
    const loanUsage = useAppSelector(selectLoanUsage);
    const defaultTermsAndConditionsAgreed = useAppSelector(
        selectAgreedToTermsAndConditions
    );
    const firstName = useAppSelector(selectFirstName);
    const assetType = useAppSelector(selectVehicleAssetType);
    const signupOptions = useAppSelector(selectSignupOptions);
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { postcode } = address;

    const customerEmail = useSelector((state: RootState) => selectEmail(state));

    const ridingTypes = [
        EVehicleAssetType.MOTORBIKES_REG,
        EVehicleAssetType.MOTORBIKES_UNREG,
        EVehicleAssetType.MOTORCYCLE,
    ];

    const caravanTypes = [
        EVehicleAssetType.CARAVAN,
        EVehicleAssetType.CARAVANS,
        EVehicleAssetType.CARAVAN_UNSEC,
    ];

    const noVehicleAssetTypes = [EVehicleAssetType.PERSONAL_LOAN];

    const title = ((): string => {
        if (assetType && ridingTypes.includes(assetType)) {
            return "Let's Go Riding!";
        } else if (assetType && caravanTypes.includes(assetType)) {
            return "Let's Go Camping!";
        } else if (assetType && noVehicleAssetTypes.includes(assetType)) {
            return "Let's Get Started!";
        } else {
            return "Let's Go Driving!";
        }
    })();

    // ***** Handlers *****
    function handleLoginClick(): void {
        dispatch(setModal(EModals.LOGIN));
    }

    function isReadyToSendCode(): boolean {
        if (
            loanUsage === ELoanUsage.BUSINESS &&
            !defaultTermsAndConditionsAgreed.isDefaultPrivacyAgreed?.agreed
        ) {
            return false;
        }
        if (!firstName || firstName.length <= 3) {
            // Minimum 3 characters
            return false;
        }

        if (signupOptions?.zipcode) {
            // Postcode is required for ZIP_CODE_SIGNUP
            if (!postcode || postcode.length < 4) {
                return false;
            }
        }

        if (signupOptions?.customerEmail) {
            if (!customerEmail || !isEmail(customerEmail)) {
                return false;
            }
        }
        return true;
    }

    // ***** Render *****
    return (
        <ModalComponent
            baseId={baseId}
            className='relative'
            data-testid={testId}
        >
            {/* Header Text */}
            <ModalHead>
                <ModalHeader title={title} baseId='Login' />
                <ModalSubHeader>
                    We will send you a one time passcode. <br />
                    No spam, promise.
                </ModalSubHeader>
            </ModalHead>
            <FirstName baseId={baseId} showErrors={false} />
            <MobileNumber baseId={baseId} showErrors={false} />
            {/* Show postcode for signup for specific user flow*/}
            {signupOptions?.zipcode && (
                <PostCode baseId={baseId} showErrors={false} />
            )}
            {signupOptions?.customerEmail && (
                <Email baseId={baseId} showErrors={false} />
            )}
            {/* Only display the default privacy checkbox for the Business applications */}
            {loanUsage === ELoanUsage.BUSINESS && (
                <>
                    <TermsAndConditions baseId={baseId} />
                    {/* <InsuranceOptIn baseId={baseId} /> */}
                </>
            )}
            <LoginCode
                baseId={baseId}
                showErrors={false}
                disabled={!isReadyToSendCode()}
            />

            <>
                <Button
                    onClick={handleLoginClick}
                    id={`${baseId}-button-signUp`}
                    title='Login'
                    type={EButtonType.SECONDARY_OUTLINE}
                />
            </>
        </ModalComponent>
    );
}
